'use client';

import { useTranslation } from '@/lib/i18n/i18n.client';
import { cn } from '@/lib/utils';

import { Product } from '@/blocks/PricingCards/product';

import iconCoinsHandSrc from '~/icons/coins-hand.svg';
import iconShieldSrc from '~/icons/shield-tick.svg';
import iconAlipaySrc from '~/svg/colorfulPayments/IconAlipay.svg';
import iconAmexSrc from '~/svg/colorfulPayments/IconAmex.svg';
import iconApplePaySrc from '~/svg/colorfulPayments/IconApplePay.svg';
import iconBitcoinSrc from '~/svg/colorfulPayments/IconBitcoin.svg';
import iconEtheriumSrc from '~/svg/colorfulPayments/IconEtherium.svg';
import iconGooglePaySrc from '~/svg/colorfulPayments/IconGooglePay.svg';
import iconMastercardSrc from '~/svg/colorfulPayments/IconMastercard.svg';
const iconPaypalSrc = '/svg/colorfulPayments/IconPaypalF.svg';
import iconTetherSrc from '~/svg/colorfulPayments/IconTether.svg';
const iconUnionPaySrc = '/svg/colorfulPayments/IconUnionPay.svg';
import iconVisaSrc from '~/svg/colorfulPayments/IconVisa.svg';
import { SvgIcon } from '@/components/SvgIcon';

const productsToExcludeCrypto: Product[] = [
  'datacenter_ip',
  'isp_gb',
  'isp_ip',
  'dedicated_isp'
];

type PaymentMethodsProps = {
  product: Product;
  title?: string;
  description?: string;
  showMoneyBack?: boolean;
  classNames?: {
    root?: string;
  };
};

export function PaymentMethods({
  product,
  title,
  description,
  showMoneyBack,
  classNames
}: PaymentMethodsProps) {
  const { texts, lang } = useTranslation();

  return (
    <div
      className={cn(
        'dark:text-gray-dark-400 mt-9 flex w-full flex-col items-start justify-between gap-6 md:mt-[3.75rem] md:flex-row md:gap-6',
        classNames?.root
      )}
    >
      {showMoneyBack && (
        <div>
          <p className='text-body-m flex gap-2 text-left'>
            <SvgIcon src={iconCoinsHandSrc} className='h-4 w-4' />
            {texts.pricing_cards.money_back.money_back}
          </p>
        </div>
      )}
      <div
        className={cn(
          showMoneyBack
            ? 'flex flex-col gap-6 md:items-end'
            : 'flex w-full flex-col justify-between gap-6 md:flex-row'
        )}
      >
        <div className='w-fit'>
          <div className={cn(showMoneyBack ? 'md:text-right' : 'text-left')}>
            <p
              className={cn(
                'text-body-m flex flex-row items-center gap-2',
                showMoneyBack && 'md:justify-end'
              )}
            >
              <SvgIcon src={iconShieldSrc} className='h-4 w-4' />

              {title || texts.pricing_cards.payment_methods.ssl_secure_payment}
            </p>
            <p className='text-body-s'>
              {description ||
                texts.pricing_cards.payment_methods.ssl_protected_information}
            </p>
          </div>
        </div>
        <div className='flex h-fit flex-wrap justify-start gap-2 md:justify-evenly'>
          {lang === 'zh-cn' && (
            <>
              <PaymentMethodWrapper>
                <SvgIcon src={iconAlipaySrc} asImg />
              </PaymentMethodWrapper>

              <PaymentMethodWrapper>
                <SvgIcon src={iconUnionPaySrc} asImg />
              </PaymentMethodWrapper>

              <PaymentMethodWrapper>
                <SvgIcon src={iconTetherSrc} asImg />
              </PaymentMethodWrapper>
            </>
          )}
          <PaymentMethodWrapper>
            <SvgIcon src={iconVisaSrc} asImg />
          </PaymentMethodWrapper>
          <PaymentMethodWrapper>
            <SvgIcon src={iconMastercardSrc} asImg />
          </PaymentMethodWrapper>

          <PaymentMethodWrapper>
            <SvgIcon src={iconPaypalSrc} asImg />
          </PaymentMethodWrapper>

          <PaymentMethodWrapper>
            <SvgIcon src={iconAmexSrc} asImg />
          </PaymentMethodWrapper>

          {!productsToExcludeCrypto.includes(product) && (
            <PaymentMethodWrapper>
              <SvgIcon src={iconBitcoinSrc} asImg />
            </PaymentMethodWrapper>
          )}
          {!productsToExcludeCrypto.includes(product) && (
            <PaymentMethodWrapper>
              <SvgIcon src={iconEtheriumSrc} asImg />
            </PaymentMethodWrapper>
          )}
          <PaymentMethodWrapper>
            <SvgIcon src={iconApplePaySrc} asImg />
          </PaymentMethodWrapper>

          <PaymentMethodWrapper>
            <SvgIcon src={iconGooglePaySrc} asImg />
          </PaymentMethodWrapper>
        </div>
      </div>
    </div>
  );
}

function PaymentMethodWrapper({ children }: { children: React.ReactNode }) {
  return (
    <div className='rounded-[5px] border border-solid border-gray-200'>
      {children}
    </div>
  );
}
